import { RemoteMultiSelect } from "../../../../../-components/remote-multi-select"
import {
  Alert,
  Button,
  Checkbox,
  CheckboxProps,
  MultiSelect,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core"
import { ApprovalConfigNode } from "./shared"
import { PathProxy } from "../../../../../utils/forms-proxy"
import { AbsenceUserWithEmailInfoSelect } from "../../../../../-components/AbsenceUserWithEmailInfoSelect"
import { userSelectRenderOptionWithEmailAddressWarning } from "../../../../../-components/userSelectRenderOptionWithEmailAddressWarning"
import {
  AbsentApprovalConfig,
  AbsentApprovalConfigNodeDetails_Case_DepartmentRoleApproval,
  AbsentApprovalConfigNodeDetails_Case_NotifyUsers,
  AbsentApprovalConfigNodeDetails_Case_ProductionRoleApproval,
  AbsentApprovalConfigNodeDetails_Case_StaticApproval,
} from "../../../../../@client/Theasoft_Absences_Config"
import { useTranslation } from "react-i18next"
import { useUpdateOrCreateApprovalConfigForm } from "./useUpdateOrCreateApprovalConfigForm"
import { DepartmentFunction } from "../../../../../orval/gen"
import { useState } from "react"
import { OrganisationTreeModal } from "../../organisation/-component"

function RejectionIsFinalCheckbox(props: CheckboxProps) {
  const { t } = useTranslation("app_server")
  return (
    <Checkbox
      label={
        <Tooltip
          multiline
          w={300}
          label={<Text>{t("RejectionIsFinalCheckboxText")}</Text>}
        >
          <Text size="sm">{t("RejectionIsFinal")}</Text>
        </Tooltip>
      }
      {...props}
    />
  )
}

function CanChangeFinalDecisionCheckbox(props: CheckboxProps) {
  const { t } = useTranslation("app_server")
  return (
    <Checkbox
      label={
        <Tooltip
          multiline
          w={300}
          label={<Text>{t("CanChangeFinalDecisionCheckboxText")}</Text>}
        >
          <Text size="sm">{t("CanChangeFinalDecision")}</Text>
        </Tooltip>
      }
      {...props}
    />
  )
}

export function StaticApproversNodeContent({ id }: ApprovalConfigNode) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const { t } = useTranslation("app_server")

  const nodeIndex = form.values.nodes.findIndex((v) => v.nodeId === id)
  const node = form.values.nodes[nodeIndex]
  if (nodeIndex === undefined || node === undefined) {
    return <Text c="red">{t("NodeNotFoundInForm")}</Text>
  }

  // TODO this is not typesafe
  const path = _.nodes[nodeIndex].details as PathProxy<
    AbsentApprovalConfig,
    AbsentApprovalConfigNodeDetails_Case_StaticApproval
  >
  return (
    <>
      <AbsenceUserWithEmailInfoSelect
        label={t("UserAndGroups")}
        description={t("UserAndGroupsDescription")}
        placeholder={t("UserAndGroupsPlaceholder")}
        renderOption={userSelectRenderOptionWithEmailAddressWarning}
        {...form.getInputProps(path.Fields.approvers._PATH_)}
      />
      <Textarea
        label={t("NoteForApprover")}
        placeholder={t("NoteForApproverPlaceholder")}
        {...form.getInputProps(path.Fields.noteForApprovers._PATH_)}
      />
      <RejectionIsFinalCheckbox
        {...form.getInputProps(path.Fields.rejectionIsFinal._PATH_, {
          type: "checkbox",
        })}
      />
      <CanChangeFinalDecisionCheckbox
        {...form.getInputProps(path.Fields.canChangeFinalDecision._PATH_, {
          type: "checkbox",
        })}
      />
    </>
  )
}

export function NotifyUsersPropertyEditors({ id }: ApprovalConfigNode) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()
  const { t } = useTranslation("app_server")

  const nodeIndex = form.values.nodes.findIndex((v) => v.nodeId === id)
  const node = form.values.nodes[nodeIndex]
  if (nodeIndex === undefined || node === undefined) {
    return <Text c="red">{t("NodeNotFoundInForm")}</Text>
  }

  // TODO this is not typesafe
  const path = _.nodes[nodeIndex].details as PathProxy<
    AbsentApprovalConfig,
    AbsentApprovalConfigNodeDetails_Case_NotifyUsers
  >
  return (
    <>
      <AbsenceUserWithEmailInfoSelect
        label={t("UserAndGroups")}
        description={t("UserAndGroupsToBeInformed")}
        placeholder={t("UserAndGroupsPlaceholder")}
        renderOption={userSelectRenderOptionWithEmailAddressWarning}
        {...form.getInputProps(path.Fields.usersIds._PATH_)}
      />
    </>
  )
}

export function ProductionRoleNodeContent({ id }: ApprovalConfigNode) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const { t } = useTranslation("app_server")

  const nodeIndex = form.values.nodes.findIndex((v) => v.nodeId === id)
  const node = form.values.nodes[nodeIndex]
  if (nodeIndex === undefined || node === undefined) {
    return <Text c="red">{t("NodeNotFoundInForm")}</Text>
  }

  // get actual node and use match expression to safely narrow type?
  const path = _.nodes[nodeIndex].details as PathProxy<
    AbsentApprovalConfig,
    AbsentApprovalConfigNodeDetails_Case_ProductionRoleApproval
  >

  return (
    <>
      <RemoteMultiSelect<"/api/RoleType/GetLeadingRoleTypes", number>
        label={t("ProductionRoles")}
        description={t("ProductionRolesThatCanApprove")}
        placeholder={t("SelectOneOrMoreProductionRoles")}
        url="/api/RoleType/GetLeadingRoleTypes"
        map={(value) => ({
          value: value.roleTypeId.toString(),
          label: `${value.name}${value.isProductionTeam ? ` (${t("Productionteam")})` : ""}`,
        })}
        toValue={(x) => parseInt(x)}
        fromValue={(x) => x.toString()}
        {...form.getInputProps(path.Fields.roleTypeIds._PATH_)}
      />
      <Alert>{t("ProductionTeamAlert")}</Alert>
      <Textarea
        label={t("NoteForApprover")}
        placeholder={t("NoteForApproverPlaceholder")}
        {...form.getInputProps(path.Fields.noteForApprovers._PATH_)}
      />
      <RejectionIsFinalCheckbox
        {...form.getInputProps(path.Fields.rejectionIsFinal._PATH_, {
          type: "checkbox",
        })}
      />
      <CanChangeFinalDecisionCheckbox
        {...form.getInputProps(path.Fields.canChangeFinalDecision._PATH_, {
          type: "checkbox",
        })}
      />
    </>
  )
}

export function DepartmentRoleNodeContent({ id }: ApprovalConfigNode) {
  const [form, _] = useUpdateOrCreateApprovalConfigForm()

  const { t } = useTranslation("app_server")

  const departmentFunctions = Object.keys(DepartmentFunction)
    .filter((v) => v !== DepartmentFunction.HeadOfWorkarea)
    .map((v) => ({
      value: v,
      label: t(`DepartmentFunction_${v as DepartmentFunction}`),
    }))

  const workareaFunctions = Object.keys(DepartmentFunction)
    .filter((v) => v !== DepartmentFunction.HeadOfDepartment)
    .map((v) => ({
      value: v,
      label: t(`DepartmentFunction_${v as DepartmentFunction}`),
    }))

  const nodeIndex = form.values.nodes.findIndex((v) => v.nodeId === id)
  const node = form.values.nodes[nodeIndex]
  if (nodeIndex === undefined || node === undefined) {
    return <Text c="red">{t("NodeNotFoundInForm")}</Text>
  }

  // get actual node and use match expression to safely narrow type?
  const path = _.nodes[nodeIndex].details as PathProxy<
    AbsentApprovalConfig,
    AbsentApprovalConfigNodeDetails_Case_DepartmentRoleApproval
  >
  const [openModal, setOpenModal] = useState(false)

  return (
    <>
      <OrganisationTreeModal
        opened={openModal}
        onClose={() => setOpenModal(false)}
        size="xl"
      />
      <Textarea
        label={t("NoteForApprover")}
        placeholder={t("NoteForApproverPlaceholder")}
        {...form.getInputProps(path.Fields.noteForApprovers._PATH_)}
      />
      <RejectionIsFinalCheckbox
        {...form.getInputProps(path.Fields.rejectionIsFinal._PATH_, {
          type: "checkbox",
        })}
      />
      <CanChangeFinalDecisionCheckbox
        {...form.getInputProps(path.Fields.canChangeFinalDecision._PATH_, {
          type: "checkbox",
        })}
      />

      <Button onClick={() => setOpenModal(true)} variant="subtle">
        {t("DepartmentFunctions_ShowPersonsWithFunctions")}
      </Button>
      <MultiSelect
        label={t("ResponsiblesInDepartment")}
        description={t("ResponsiblesInDepartmentDescription")}
        clearable={false}
        {...form.getInputProps(
          path.Fields.departmentMembersWithFunctions._PATH_
        )}
        data={departmentFunctions}
      />
      <MultiSelect
        label={t("ResponsiblesInWorkarea")}
        description={t("ResponsiblesInWorkareaDescription")}
        clearable={false}
        {...form.getInputProps(path.Fields.workareaMembersWithFunctions._PATH_)}
        data={workareaFunctions}
      />
      {/* <Checkbox
        label="Eigenen Antrag genehmigen"
        description="Wenn aktiv, werden Personen bei eigenen Antraegen als Genehmiger hinzugefügt, sofern sie die entsprechenden Funktionen in der Abteilung oder dem Arbeitsbereich haben."
        {...form.getInputProps(path.Fields.includeRequestingPerson._PATH_, {
          type: "checkbox",
        })}
      /> */}
    </>
  )
}
